import { useCallback } from 'react';
import type { DragEndEvent } from '@dnd-kit/core';
import type { BoardCoreFragment } from '../../../generated/graphql';
import { cardIdOf, columnIdOf } from './drag-ids';
import { columnOfCard, sortedByPosition, tailCardId } from './board-order';
import { dragTrace } from './drag-trace';

export interface CardMoveArgs {
  cardId: string;
  toColumnId: string;
  afterCardId?: string;
}

export interface ColumnMoveArgs {
  columnId: string;
  afterColumnId?: string;
}

interface UseBoardDragHandlersArgs {
  // Nullable so callers can invoke the hook unconditionally (Rules of
  // Hooks) before the board query has resolved; the handler is a no-op
  // until a board is available.
  board: BoardCoreFragment | null | undefined;
  onCardMove: (args: CardMoveArgs) => void;
  onColumnMove: (args: ColumnMoveArgs) => void;
}

// Converts dnd-kit's "active dropped onto over" event into our
// `afterId`-shaped move args. Two flavors:
//
// 1) Within a single list (e.g. dragging column A to column B):
//    - Moving RIGHT (oldIdx < newIdx) → place A after B
//    - Moving LEFT  (oldIdx > newIdx) → place A in B's slot, i.e.
//      after whatever is left of B (or undefined if B is first)
//
// 2) Inserting from another list (cross-column card drag): we take
//    the over card's slot (insert BEFORE it).
//
// `undefined` is a meaningful result — it means "place first" — so a drag that
// cannot be resolved to a real reorder reports NO_MOVE instead.
const NO_MOVE = Symbol('no-move');

function afterIdForMoveWithin<T extends { _id: string }>(
  sortedItems: T[],
  activeId: string,
  overId: string
): string | undefined | typeof NO_MOVE {
  const oldIdx = sortedItems.findIndex((i) => i._id === activeId);
  const newIdx = sortedItems.findIndex((i) => i._id === overId);
  // Either id missing from this list, or the item was dropped on its own
  // slot. Neither is a move. This previously returned `overId`, which
  // silently anchored the item after whatever it happened to be over — a
  // wrong placement dressed up as a successful one.
  if (oldIdx < 0 || newIdx < 0 || oldIdx === newIdx) return NO_MOVE;
  if (oldIdx < newIdx) return overId;
  return newIdx > 0 ? sortedItems[newIdx - 1]._id : undefined;
}

function afterIdForInsertBefore<T extends { _id: string }>(
  sortedItems: T[],
  overId: string
): string | undefined {
  const overIdx = sortedItems.findIndex((i) => i._id === overId);
  if (overIdx <= 0) return undefined;
  return sortedItems[overIdx - 1]._id;
}

export function useBoardDragHandlers(args: UseBoardDragHandlersArgs) {
  const { board, onCardMove, onColumnMove } = args;

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (!board) return;
      const { active, over } = event;
      if (!over) return;
      if (active.id === over.id) return;

      const activeType = active.data.current?.type;
      const overType = over.data.current?.type;

      if (activeType === 'card') {
        const cardId = cardIdOf(active.id);
        const sourceColumn = columnOfCard(board.columns, cardId);

        if (overType === 'card') {
          const overCardId = cardIdOf(over.id);
          const overColumnId = over.data.current?.columnId as string;
          const targetCol = board.columns.find((c) => c._id === overColumnId);
          if (!targetCol) return;
          const sortedTarget = sortedByPosition(targetCol.cards);

          let afterCardId: string | undefined | typeof NO_MOVE;
          if (sourceColumn?._id === overColumnId) {
            afterCardId = afterIdForMoveWithin(
              sortedTarget,
              cardId,
              overCardId
            );
          } else {
            afterCardId = afterIdForInsertBefore(sortedTarget, overCardId);
          }
          if (afterCardId === NO_MOVE) {
            dragTrace('commit', { kind: 'card', result: 'NO_MOVE' });
            return;
          }
          dragTrace('commit', {
            kind: 'card',
            via: sourceColumn?._id === overColumnId ? 'within' : 'insertBefore',
            card: cardId,
            to: overColumnId,
            after: afterCardId,
          });
          onCardMove({ cardId, toColumnId: overColumnId, afterCardId });
        } else if (overType === 'column') {
          // Dropped on the column body (no card under cursor) — append at tail.
          const overColumnId = columnIdOf(over.id);
          const col = board.columns.find((c) => c._id === overColumnId);
          const afterCardId = tailCardId(col?.cards ?? []);
          dragTrace('commit', {
            kind: 'card',
            via: 'columnTail',
            card: cardId,
            to: overColumnId,
            after: afterCardId,
          });
          onCardMove({ cardId, toColumnId: overColumnId, afterCardId });
        }
      } else if (activeType === 'column') {
        const columnId = columnIdOf(active.id);
        if (overType === 'column') {
          const overColumnId = columnIdOf(over.id);
          const sorted = sortedByPosition(board.columns);
          const afterColumnId = afterIdForMoveWithin(
            sorted,
            columnId,
            overColumnId
          );
          if (afterColumnId === NO_MOVE) {
            dragTrace('commit', { kind: 'column', result: 'NO_MOVE' });
            return;
          }
          dragTrace('commit', {
            kind: 'column',
            column: columnId,
            after: afterColumnId,
          });
          onColumnMove({ columnId, afterColumnId });
        }
      }
    },
    [board, onCardMove, onColumnMove]
  );

  return { onDragEnd };
}
